import type { Message } from 'ai';
import { useCallback, useState } from 'react';
import { StreamingMessageParser } from '~/lib/runtime/message-parser';
import { workbenchStore } from '~/lib/stores/workbench';
import { createScopedLogger } from '~/utils/logger';
import { selectionStore } from '~/lib/persistence/selectionStore';

const logger = createScopedLogger('useMessageParser');

const messageParser = new StreamingMessageParser({
  callbacks: {
    onArtifactOpen: async (data) => {
      // const { framework, language } = chatStore.get();
      logger.trace('onArtifactOpen', data);

      // console.log('[refresh] onArtifactOpen', data);

      workbenchStore.showWorkbench.set(true);
      workbenchStore.addArtifact(data);
    },
    onArtifactClose: (data) => {
      logger.trace('onArtifactClose');

      workbenchStore.updateArtifact(data, { closed: true });
    },
    onActionOpen: async (data) => {
      logger.trace('onActionOpen', data.action);

      const framework = selectionStore.get().framework;

      if (data.action.type !== 'shell' && framework !== 'flutter') {
        console.log('[onActionOpen] data.index', data.index);
        console.log('[onActionOpen] data.initialMessagesLength', data.initialMessagesLength);

        if (data.index && data.index > (data.initialMessagesLength ?? 0) - 1) {
          console.log('[onActionOpen] running the action');
          workbenchStore.addAction(data);
        } else {
          workbenchStore.addHistoryAction(data);
        }
      }
    },

    onActionClose: (data) => {
      logger.trace('onActionClose', data.action);

      // console.log('[refresh] onActionClose', data);

      if (
        'filePath' in data.action &&
        'content' in data.action &&
        typeof data.action.content === 'string' &&
        data.action.filePath.includes('main.dart')
      ) {
        console.log('Flutter code content:', data.action.content);
        localStorage.setItem('flutterCode', data.action.content);
      } else {
        console.log('[onActionClose] data.index', data.index);
        console.log('[onActionClose] data.initialMessagesLength', data.initialMessagesLength);

        // run the action only when it is generated by ai and not loaded from the history
        if (data.index && data.index > (data.initialMessagesLength ?? 0) - 1) {
          console.log('[onActionClose] running the action');

          if (data.action.type === 'shell') {
            workbenchStore.addAction(data);
          }

          workbenchStore.runAction(data);
        } else {
          workbenchStore.addHistoryAction(data);
        }
      }

      // update the fileActions
      const fileActions = messageParser.getFileActions() || [];
      let isFileActionUpdated: boolean = false;
      const updatedFileActions = fileActions.map((fileAction) => {
        if (
          (data.action.type === 'file' && fileAction.id === data.action.filePath) ||
          (data.action.type === 'shell' && fileAction.id === data.action.content)
        ) {
          isFileActionUpdated = true;
          return { ...fileAction, content: data.action.content };
        }

        return fileAction;
      });

      const id = data.action.type === 'file' ? data.action.filePath : data.action.content;

      console.log('[onActionClose] isFileActionUpdated', isFileActionUpdated, id);

      if (!isFileActionUpdated) {
        console.log('[onActionClose] adding new file action', data.action);

        if (data.action.type === 'shell') {
          updatedFileActions.push({
            ...data.action,
            id,
          });
        } else {
          // find the index of the last file action
          const lastFileActionIndex = updatedFileActions.findLastIndex((action) => action.type === 'file');

          // push the file action just before shell actions have started.
          updatedFileActions.splice(lastFileActionIndex + 1, 0, {
            ...data.action,
            id,
          });
        }
      }

      messageParser.setFileActions(updatedFileActions);
    },
  },
});

export function useMessageParser() {
  const [parsedMessages, setParsedMessages] = useState<{ [key: number]: string }>({});

  const fileActions = messageParser.getFileActions();

  const parseMessages = useCallback((messages: Message[], isLoading: boolean, initialMessagesLength: number) => {
    console.log('[refresh] messages from useMessageParser top: ', messages);

    let reset = false;

    if (import.meta.env.DEV && !isLoading) {
      reset = true;
      messageParser.reset();
    }

    for (const [index, message] of messages.entries()) {
      if (message.role === 'assistant') {
        console.log('[refresh] message from useMessageParser for loop: ', index, message);

        const newParsedContent = messageParser.parse(message.id, message.content, index, initialMessagesLength);

        // console.log('newParsedContent from useMessageParser', newParsedContent);

        setParsedMessages((prevParsed) => ({
          ...prevParsed,
          [index]: !reset ? (prevParsed[index] || '') + newParsedContent : newParsedContent,
        }));
      }
    }
  }, []);

  return { parsedMessages, parseMessages, fileActions };
}
